import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useLostSales } from "../../context/lostSalesContext";
import LostSalesResultView from "../lost-sales-result-view";
import { describeRevenueEstimate } from "../../utils/lostSalesRevenue";
import {
  buildProductLabelById,
  formatMetric,
  resolveBulkProductLabel,
} from "../../utils/lostSalesBulk";

const LostSalesResultModal = () => {
  const {
    resultDialogOpen,
    setResultDialogOpen,
    result,
    bulkResult,
    bulkScopes,
    selectedInventory,
    detailResult,
    detailLoading,
    openBulkDetail,
    closeDetail,
    error,
    clearError,
  } = useLostSales();

  const productLabelById = buildProductLabelById(
    (bulkScopes || []).map((scope) => scope?.product).filter(Boolean),
  );

  const handleClose = () => {
    closeDetail();
    clearError();
    setResultDialogOpen(false);
  };

  const showBulk = Boolean(bulkResult?.bulk && !detailResult);
  const showDetail = Boolean(detailResult || (!bulkResult && result));
  const open =
    resultDialogOpen &&
    (result?.status === "complete" || bulkResult?.status === "complete");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={showBulk && !detailResult ? "lg" : "md"}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 600, pb: 0.5 }}>
        {detailResult
          ? "Lost Sales Detail"
          : showBulk
            ? "Lost Sales Bulk Results"
            : "Lost Sales Analysis"}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            <Typography sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
              {error.title}
            </Typography>
            <Typography sx={{ fontFamily: "Poppins", fontSize: 14 }}>
              {error.body}
            </Typography>
          </Alert>
        )}

        {detailLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!detailLoading && showDetail && (
          <LostSalesResultView
            result={detailResult || result}
            selectedInventory={
              detailResult
                ? {
                    product: detailResult.scope?.product_name ||
                      detailResult.scope?.product,
                    sku: detailResult.scope?.sku,
                    warehouse: {
                      name: bulkResult?.shared?.warehouse_name,
                      _id: bulkResult?.shared?.warehouse_id,
                    },
                  }
                : selectedInventory
            }
          />
        )}

        {!detailLoading && showBulk && (
          <BulkResultsTable
            bulkResult={bulkResult}
            productLabelById={productLabelById}
            onOpenDetail={openBulkDetail}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {detailResult && (
          <Button onClick={closeDetail}>Back to results</Button>
        )}
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkResultsTable = ({ bulkResult, onOpenDetail, productLabelById }) => {
  const summary = bulkResult.summary || {};
  const shared = bulkResult.shared || {};
  const rows = bulkResult.results || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, 1fr)",
          },
          gap: 1.5,
        }}
      >
        <SummaryCard
          label="Requested"
          value={summary.requested}
        />
        <SummaryCard
          label="Succeeded"
          value={summary.succeeded}
          color="#166534"
        />
        <SummaryCard
          label="Failed"
          value={summary.failed}
          color={summary.failed ? "#991B1B" : undefined}
        />
        <SummaryCard
          label="Total Lost Sales"
          value={formatMetric(summary.total_estimated_lost_units)}
        />
        <SummaryCard
          label="Warehouse"
          value={shared.warehouse_name || shared.warehouse_id || "—"}
        />
        <SummaryCard
          label="Date Range"
          value={`${shared.start_date || "—"} → ${shared.end_date || "—"}`}
        />
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Lost Sales</TableCell>
              <TableCell align="right">Est. Lost Revenue</TableCell>
              <TableCell align="right">Stockout Days</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const productLabel = resolveBulkProductLabel(
                row.scope,
                productLabelById,
              );
              const skuLabel =
                row.scope?.analysis_level === "product"
                  ? "All child SKUs"
                  : row.scope?.sku || "—";
              return (
                <TableRow key={`${row.scope?.product}-${row.scope?.sku}-${index}`}>
                  <TableCell>{productLabel}</TableCell>
                  <TableCell>{skuLabel}</TableCell>
                  <TableCell align="right">
                    {row.status === "complete"
                      ? formatMetric(row.metrics?.estimated_lost_units)
                      : "—"}
                  </TableCell>
                  <TableCell
                    align="right"
                    title={
                      row.status === "complete"
                        ? describeRevenueEstimate(row.revenue_estimate).note || undefined
                        : undefined
                    }
                  >
                    {row.status === "complete"
                      ? (() => {
                          const view = describeRevenueEstimate(row.revenue_estimate);
                          return view.state === "incomplete"
                            ? `${view.amountLabel}*`
                            : view.amountLabel;
                        })()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {row.status === "complete"
                      ? formatMetric(row.metrics?.stockout_days)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {row.status === "complete" ? (
                      <Chip size="small" color="success" label="Succeeded" />
                    ) : (
                      <Chip
                        size="small"
                        color="error"
                        label={row.code || "Failed"}
                        title={row.message}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.status === "complete" && (
                      <Button size="small" onClick={() => onOpenDetail(row)}>
                        View
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "var(--bg-highlight, rgba(0,0,0,0.02))",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        color: "text.secondary",
        fontFamily: "Poppins",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 600,
        fontFamily: "Poppins",
        color: color || "text.primary",
        mt: 0.5,
      }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

export default LostSalesResultModal;
